import { useCallback, useMemo, useRef } from 'react';
import useLeavingItems from 'hooks/useLeavingItems';

const leaveKey = (missionId, tempWorkerId) => `${missionId}:${tempWorkerId}`;

/**
 * The candidates of the open mission that fold away from their list before a
 * refusal or an "en attente" moves them (hooks/useLeavingItems), each call
 * taking the mission id first.
 *
 * Entries are keyed by mission: the same temp worker applies to other
 * missions, whose card must not show this exit. A move whose mission is no
 * longer the open one when the fold ends is dropped: it would land in the
 * other mission's lists, and the server already holds the state the first
 * mission reloads.
 */
const useMissionCandidatesLeaving = (currentMissionId) => {
  const { leave, cancel, isLeaving, onLeaveEnd } = useLeavingItems();
  const currentMissionIdRef = useRef(currentMissionId);
  currentMissionIdRef.current = currentMissionId;

  const leaveCandidate = useCallback(
    (missionId, tempWorkerId, commit) =>
      leave(leaveKey(missionId, tempWorkerId), () => {
        if (String(currentMissionIdRef.current) !== String(missionId)) return;
        commit();
      }),
    [leave],
  );

  const cancelCandidate = useCallback(
    (missionId, tempWorkerId) => cancel(leaveKey(missionId, tempWorkerId)),
    [cancel],
  );

  const isCandidateLeaving = useCallback(
    (missionId, tempWorkerId) => isLeaving(leaveKey(missionId, tempWorkerId)),
    [isLeaving],
  );

  const onCandidateLeaveEnd = useCallback(
    (missionId, tempWorkerId) => onLeaveEnd(leaveKey(missionId, tempWorkerId)),
    [onLeaveEnd],
  );

  return useMemo(
    () => ({
      leave: leaveCandidate,
      cancel: cancelCandidate,
      isLeaving: isCandidateLeaving,
      onLeaveEnd: onCandidateLeaveEnd,
    }),
    [leaveCandidate, cancelCandidate, isCandidateLeaving, onCandidateLeaveEnd],
  );
};

export default useMissionCandidatesLeaving;
